import api from './api';
import { buildApiUrl, getAuthHeaders } from './apiConfig';

/**
 * Fetch conversation history for a specific user and agent
 * @param {Object} params - Parameters for fetching conversations
 * @param {string} params.userId - The user UUID
 * @param {string} params.agentId - The agent UUID
 * @returns {Promise} Response with conversations list
 */
export const getConversationHistory = async ({ userId, agentId }) => {
  try {
    // POST request with payload
    // Note: curl example shows "userUUid" (double U) - using exact name from curl
    const payload = {
      userUUid: userId, // Using exact name from curl example (double U)
      agentUuid: agentId
    };

    // Get auth headers
    const authHeaders = getAuthHeaders();
    
    const response = await fetch(buildApiUrl('/v1/conversation/list'), {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching conversation history:', error);
    throw error;
  }
};

/**
 * Fetch messages for a specific conversation
 * @param {Object} params - Parameters for fetching messages
 * @param {string} params.conversationId - The conversation ID
 * @returns {Promise} Response with messages list
 */
export const getConversationMessages = async ({ conversationId }) => {
  try {
    // New endpoint expects POST with conversationUuid in the body
    const payload = { conversationUuid: conversationId };
    const authHeaders = getAuthHeaders();

    const response = await fetch(buildApiUrl("/v1/message/list"), {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching conversation messages:", error);
    throw error;
  }
};

/**
 * Delete a conversation
 * @param {string} conversationId - The conversation ID to delete
 * @returns {Promise} Response from delete operation
 */
export const deleteConversation = async (conversationId) => {
  try {
    const response = await api.delete(`/conversation/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting conversation:', error);
    throw error;
  }
};

/**
 * Update conversation metadata (e.g., title)
 * @param {string} conversationId - The conversation ID
 * @param {Object} updates - Updates to apply
 * @returns {Promise} Response from update operation
 */
export const updateConversation = async (conversationId, updates) => {
  try {
    const response = await api.patch(`/conversation/${conversationId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating conversation:', error);
    throw error;
  }
};

